import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { MobileDateRangePicker } from '@mui/lab';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import ShowChartOutlinedIcon from '@mui/icons-material/ShowChartOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { LineChart, MarkElement } from '@mui/x-charts/LineChart';
import { ChartsReferenceLine } from '@mui/x-charts/ChartsReferenceLine';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { dispatch } from '../../../redux/store';
import { getUtilityCategories } from '../../../redux/slices/utilityRedux/utilityCategoriesRedux';
import { clearUtilityBillCostTrend, clearUtilityBillCostTrendDetails, getUtilityBillCostTrend, getUtilityBillCostTrendDetails } from '../../../redux/slices/utilityRedux/utilityCostTrendRedux';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import useSettings from '../../../hooks/useSettings';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { GLOBAL_LOCATION_ID } from '../../../utils/constants';
import { fCurrency } from '../../../utils/formatNumber';

const getDefaultDateRange = () => [moment().subtract(12, 'months').startOf('day').toDate(), moment().endOf('day').toDate()];

const toDateOnly = (value) => (value ? moment(value).format('YYYY-MM-DD') : '');

const formatDate = (value) => {
  if (!value) return '-';
  return value instanceof Date ? moment(value).format('DD MMM YYYY') : moment.utc(String(value).slice(0, 10)).format('DD MMM YYYY');
};

const getGranularityLabel = (value) => ({ daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' })[value] || value || '-';

const MetricCard = ({ label, value, helper, icon, color = 'primary' }) => (
  <Card sx={{ height: '100%', p: { xs: 2, md: 2.5 }, border: 1, borderColor: 'divider', boxShadow: 'none' }}>
    <Stack direction='row' spacing={1.5} alignItems='flex-start'>
      <Box
        sx={(theme) => ({
          display: 'grid',
          flexShrink: 0,
          placeItems: 'center',
          width: 40,
          height: 40,
          borderRadius: 1.25,
          color: theme.palette[color].dark,
          bgcolor: alpha(theme.palette[color].main, 0.12),
          '& svg': { fontSize: 20 },
        })}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant='overline' color='text.secondary'>
          {label}
        </Typography>
        <Typography variant='h5' sx={{ mt: 0.25, wordBreak: 'break-word' }}>
          {value}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {helper}
        </Typography>
      </Box>
    </Stack>
  </Card>
);

const CostAxisTooltip = ({ dataIndex, series, pointMetadata, categoryName }) => {
  if (dataIndex == null) return null;

  const points = series.map(({ id, label }) => ({ id, label, point: pointMetadata?.[id]?.[dataIndex] })).filter(({ point }) => point);

  if (!points.length) return null;

  const bucket = points[0].point;
  const isMultiLocation = points.length > 1;
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 3,
        width: isMultiLocation ? { xs: 'calc(100vw - 32px)', sm: 560 } : 240,
        maxWidth: 'calc(100vw - 32px)',
        p: 1.5,
      }}
    >
      <Typography variant='subtitle2'>{categoryName || 'Utility cost'}</Typography>
      <Typography variant='caption' color='text.secondary'>
        {bucket.displayLabel}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: isMultiLocation ? 'repeat(2, minmax(0, 1fr))' : '1fr' }, columnGap: 2, rowGap: 0.75 }}>
        {points.map(({ id, label, point }) => (
          <Box key={id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, minWidth: 0 }}>
            <Typography variant='body2' noWrap sx={{ minWidth: 0 }}>
              {point.locationName || label || id}
            </Typography>
            <Box sx={{ flexShrink: 0, textAlign: 'right' }}>
              <Typography variant='body2' noWrap>
                {fCurrency(point.totalCost)}
              </Typography>
              <Typography variant='caption' color='text.secondary' noWrap>
                {point.billCount} {point.billCount === 1 ? 'bill' : 'bills'}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
      <Typography variant='caption' color='text.secondary'>
        Click a point to see the contributing bills.
      </Typography>
    </Box>
  );
};

const ClickableMark = ({ onMarkClick, ...props }) => (
  <MarkElement {...props} onClick={(event) => onMarkClick?.({ event, seriesId: props.id, dataIndex: props.dataIndex })} style={{ cursor: 'pointer', ...props.style }} />
);

export default function CostByCategoryTrend() {
  const { themeStretch } = useSettings();
  const theme = useTheme();
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [openPicker, setOpenPicker] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);

  const selectedLocation = useSelector((state) => state.location?.selectedLocation);
  const utilityCategories = useSelector((state) => state.utilityCategories?.data || []);
  const { trend, trendLoading, trendError, details, detailsLoading, detailsError } = useSelector((state) => state.utilityCostTrend || {});

  const activeCategories = useMemo(() => utilityCategories.filter((category) => Number(category?.status) === 1), [utilityCategories]);

  const locationId = selectedLocation?.id ? Number(selectedLocation.id) : null;
  const locationName = selectedLocation?.name || 'Select a location';
  const selectedCategory = activeCategories.find((category) => Number(category.id) === Number(selectedCategoryId));

  useEffect(() => {
    dispatch(getUtilityCategories());
  }, []);

  useEffect(() => {
    if (!selectedCategoryId || !dateRange[0] || !dateRange[1] || !locationId) return;

    setIsDrawerOpen(false);
    setSelectedPoint(null);
    clearUtilityBillCostTrendDetails();
    dispatch(
      getUtilityBillCostTrend({
        locationId,
        utilityCategoryId: selectedCategoryId,
        fromDate: toDateOnly(dateRange[0]),
        toDate: toDateOnly(dateRange[1]),
      })
    );
  }, [dateRange, locationId, selectedCategoryId]);

  const pointMetadata = useMemo(() => Object.fromEntries((trend?.series || []).map((series) => [series.id, series.pointMetadata || []])), [trend]);

  const chartSeries = useMemo(
    () =>
      (trend?.series || []).map((series) => ({
        id: series.id,
        label: series.locationName,
        data: series.data,
        showMark: true,
        valueFormatter: (value) => (value == null ? '' : fCurrency(value)),
      })),
    [trend]
  );
  const chartTopMargin = chartSeries.length > 1 ? 72 : 24;
  const isGlobalLocation = locationId === GLOBAL_LOCATION_ID;
  const averageCost = useMemo(() => {
    if (isGlobalLocation) return null;

    const totalCost = Number(trend?.summary?.totalCost || 0);
    const billCount = Number(trend?.summary?.billCount || 0);
    if (!billCount) return null;

    return totalCost / billCount;
  }, [isGlobalLocation, trend]);

  const handleChartPointClick = ({ seriesId, dataIndex }) => {
    const selectedSeries = trend?.series?.find((series) => series.id === seriesId);
    const point = selectedSeries?.pointMetadata?.[dataIndex];
    if (!selectedSeries || !point) return;

    const nextPoint = {
      ...point,
      categoryName: trend.categoryName,
      locationId: selectedSeries.locationId,
      locationName: selectedSeries.locationName,
    };
    setSelectedPoint(nextPoint);
    setIsDrawerOpen(true);
    dispatch(
      getUtilityBillCostTrendDetails({
        locationId: selectedSeries.locationId,
        utilityCategoryId: trend.categoryId,
        fromDate: trend.fromDate,
        toDate: trend.toDate,
        bucketStart: point.bucketStart,
        bucketEnd: point.bucketEnd,
      })
    );
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedPoint(null);
    clearUtilityBillCostTrendDetails();
  };

  const renderReportBody = () => {
    if (!selectedCategoryId) {
      return (
        <Box sx={{ py: 10, textAlign: 'center' }}>
          <Typography variant='h6' gutterBottom>
            Select a utility category to view its cost trend
          </Typography>
          <Typography color='text.secondary'>Only active utility categories and approved bills are included.</Typography>
        </Box>
      );
    }

    if (trendLoading) {
      return (
        <Stack alignItems='center' justifyContent='center' spacing={2} sx={{ minHeight: 430 }}>
          <CircularProgress />
          <Typography color='text.secondary'>Loading utility cost trend...</Typography>
        </Stack>
      );
    }

    if (trendError) return <Alert severity='error'>{typeof trendError === 'string' ? trendError : 'Unable to load utility cost trend.'}</Alert>;

    if (!chartSeries.length || !(trend?.buckets || []).length || Number(trend?.summary?.billCount || 0) === 0) {
      return (
        <Box sx={{ py: 10, textAlign: 'center' }}>
          <Typography variant='h6' gutterBottom>
            No approved bills found for this category and date range
          </Typography>
          <Typography color='text.secondary'>Try a different category or a wider bill-date range.</Typography>
        </Box>
      );
    }

    return (
      <LineChart
        xAxis={[
          {
            scaleType: 'point',
            data: trend.buckets.map((bucket) => bucket.bucketStart),
            label: 'Bill period',
            valueFormatter: (bucketStart) => trend.buckets.find((bucket) => bucket.bucketStart === bucketStart)?.displayLabel || bucketStart,
          },
        ]}
        yAxis={[{ label: 'Approved cost' }]}
        series={chartSeries}
        colors={[theme.palette.primary.main, theme.palette.secondary.main, theme.palette.info.main, theme.palette.warning.main, theme.palette.error.main]}
        tooltip={{ trigger: 'axis' }}
        legend={{ hidden: false }}
        slots={{ axisContent: CostAxisTooltip, mark: ClickableMark }}
        slotProps={{
          axisContent: { pointMetadata, categoryName: trend.categoryName },
          mark: { onMarkClick: handleChartPointClick },
        }}
        height={460}
        margin={{ top: chartTopMargin, bottom: 80, left: 88, right: 28 }}
      >
        {averageCost != null && (
          <ChartsReferenceLine
            y={averageCost}
            label={`Average bill amount · ${fCurrency(averageCost)}`}
            labelAlign='end'
            spacing={{ x: 8, y: 4 }}
            lineStyle={{ stroke: theme.palette.warning.dark, strokeDasharray: '6 4', strokeWidth: 1.5 }}
            labelStyle={{ fill: theme.palette.warning.dark, fontSize: 12, fontWeight: 600 }}
          />
        )}
      </LineChart>
    );
  };

  const detailBills = details?.bills || [];
  const detailPaidTotal = detailBills.reduce((sum, bill) => sum + Number(bill.paidAmount || 0), 0);

  return (
    <Page title='Cost by Category Trend'>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs
          heading='Cost by Category Trend'
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.dashboard.root }, { name: 'Administration', href: PATH_DASHBOARD.dashboard.administration }, { name: 'Cost by Category Trend' }]}
          sx={{ mb: 3 }}
        />

        <Card sx={{ mb: 3, p: { xs: 2, md: 3 }, border: 1, borderColor: 'divider', boxShadow: 'none' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' alignItems={{ sm: 'center' }} spacing={1.5} sx={{ mb: 2 }}>
            <Stack direction='row' spacing={1.25} alignItems='center'>
              <Box
                sx={(theme) => ({
                  display: 'grid',
                  placeItems: 'center',
                  width: 36,
                  height: 36,
                  borderRadius: 1.25,
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                })}
              >
                <TuneOutlinedIcon fontSize='small' />
              </Box>
              <Box>
                <Typography variant='h6'>Report filters</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Choose a category and period to explore approved utility costs.
                </Typography>
              </Box>
            </Stack>
            <Chip
              icon={<LocationOnOutlinedIcon />}
              label={`Viewing: ${locationName}`}
              color={selectedLocation?.id ? 'primary' : 'default'}
              variant='outlined'
              sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
            />
          </Stack>
          <Divider sx={{ mb: 2.5 }} />
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size='small' required>
                <InputLabel id='utility-cost-category-label'>Utility category</InputLabel>
                <Select
                  labelId='utility-cost-category-label'
                  label='Utility category'
                  value={selectedCategoryId}
                  onChange={(event) => {
                    setSelectedCategoryId(event.target.value);
                    setIsDrawerOpen(false);
                    clearUtilityBillCostTrend();
                    clearUtilityBillCostTrendDetails();
                  }}
                >
                  <MenuItem value=''>Select a category</MenuItem>
                  {activeCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <MobileDateRangePicker
                open={openPicker}
                onOpen={() => setOpenPicker(true)}
                onClose={() => setOpenPicker(false)}
                value={dateRange}
                onChange={() => {}}
                onAccept={(newValue) => {
                  setDateRange((newValue || []).map((date, index) => (date ? (index === 1 ? moment(date).endOf('day').toDate() : moment(date).startOf('day').toDate()) : null)));
                }}
                renderInput={({ inputRef, inputProps, InputProps }) => (
                  <TextField
                    ref={inputRef}
                    {...inputProps}
                    InputProps={{
                      ...InputProps,
                      startAdornment: (
                        <InputAdornment position='start'>
                          <CalendarTodayIcon sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton onClick={() => setDateRange(getDefaultDateRange())} size='small'>
                            <ClearIcon sx={{ color: 'text.secondary' }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    label='Bill date range'
                    value={dateRange[0] && dateRange[1] ? `${formatDate(dateRange[0])} – ${formatDate(dateRange[1])}` : ''}
                    onClick={() => setOpenPicker(true)}
                    readOnly
                    fullWidth
                    size='small'
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ pl: { md: 1 } }}>
                <LocationOnOutlinedIcon sx={{ color: 'text.secondary' }} fontSize='small' />
                <Box>
                  <Typography variant='caption' color='text.secondary'>
                    Location context
                  </Typography>
                  <Typography variant='subtitle1'>{locationName}</Typography>
                </Box>
              </Stack>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, px: 1.5, py: 1, borderRadius: 1, bgcolor: 'background.neutral' }}>
            <Stack direction='row' spacing={1} alignItems='center'>
              <InfoOutlinedIcon sx={{ color: 'text.secondary' }} fontSize='small' />
              <Typography variant='body2' color='text.secondary'>
                {selectedCategory?.name || 'Select a category'} · {locationName} · {dateRange[0] && dateRange[1] ? `${formatDate(dateRange[0])} – ${formatDate(dateRange[1])}` : 'Select a bill date range'}
              </Typography>
            </Stack>
          </Box>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <MetricCard
              label='Selected category'
              value={selectedCategory?.name || 'Not selected'}
              helper='Active utility category'
              icon={<CategoryOutlinedIcon />}
              color='secondary'
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MetricCard
              label='Approved cost in range'
              value={fCurrency(trend?.summary?.totalCost || 0)}
              helper='Approved statuses only'
              icon={<PaymentsOutlinedIcon />}
              color='success'
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <MetricCard
              label='Approved bills in selected range'
              value={`${trend?.summary?.billCount || 0} ${Number(trend?.summary?.billCount || 0) === 1 ? 'bill' : 'bills'}`}
              helper={trend?.granularity ? `One point per ${getGranularityLabel(trend.granularity).toLowerCase()} period` : 'Choose a category to begin'}
              icon={<ReceiptLongOutlinedIcon />}
              color='info'
            />
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ p: { xs: 2, md: 3 }, border: 1, borderColor: 'divider', boxShadow: 'none', overflow: 'hidden' }}>
              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent='space-between' alignItems={{ md: 'flex-start' }} spacing={2} sx={{ mb: 2 }}>
                <Stack direction='row' spacing={1.25} alignItems='flex-start'>
                  <ShowChartOutlinedIcon sx={{ mt: 0.25, color: 'primary.main' }} />
                  <Box>
                    <Typography variant='h6'>Approved utility cost trend</Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Compare {selectedCategory?.name || 'utility'} costs across the selected location context.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                  {trend?.granularity && <Chip label={`${getGranularityLabel(trend.granularity)} view`} size='small' color='primary' variant='outlined' />}
                  <Chip label='Click a point for bill details' size='small' variant='outlined' />
                </Stack>
              </Stack>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ p: { xs: 0, md: 1 }, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.neutral' }}>
                {renderReportBody()}
              </Box>
              <Stack direction='row' spacing={1} alignItems='center' sx={{ mt: 1.5 }}>
                <InfoOutlinedIcon sx={{ color: 'text.secondary' }} fontSize='small' />
                <Typography variant='caption' color='text.secondary'>
                  Full bill amounts are assigned to the bill-date period. Empty periods are shown as zero.
                </Typography>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Drawer anchor='right' open={isDrawerOpen} onClose={closeDrawer} PaperProps={{ sx: { width: { xs: '100%', sm: 760, md: 1000 }, bgcolor: 'background.default' } }}>
        <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={2}>
            <Stack direction='row' spacing={1.5} alignItems='flex-start'>
              <Box
                sx={(theme) => ({
                  display: 'grid',
                  flexShrink: 0,
                  placeItems: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                })}
              >
                <ReceiptLongOutlinedIcon />
              </Box>
              <Box>
                <Typography variant='overline' color='text.secondary'>
                  Contributing bills
                </Typography>
                <Typography variant='h6'>
                  {selectedPoint?.categoryName || selectedCategory?.name || 'Utility'} · {selectedPoint?.locationName || locationName} · {selectedPoint?.displayLabel || 'Selected chart bucket'}
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Approved bills included in this chart period.
                </Typography>
              </Box>
            </Stack>
            <IconButton onClick={closeDrawer} aria-label='Close bill details'>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2.5 }}>
            <Box sx={{ flex: 1, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.neutral' }}>
              <Typography variant='overline' color='text.secondary'>
                Period cost
              </Typography>
              <Typography variant='h6'>{fCurrency(details?.totalCost ?? selectedPoint?.totalCost ?? 0)}</Typography>
            </Box>
            <Box sx={{ flex: 1, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1.5, bgcolor: 'background.neutral' }}>
              <Typography variant='overline' color='text.secondary'>
                Bills in period
              </Typography>
              <Typography variant='h6'>{details?.billCount ?? selectedPoint?.billCount ?? 0}</Typography>
            </Box>
          </Stack>
        </Box>

        {detailsLoading && (
          <Stack alignItems='center' justifyContent='center' spacing={2} sx={{ minHeight: 220 }}>
            <CircularProgress />
            <Typography color='text.secondary'>Loading contributing bills...</Typography>
          </Stack>
        )}
        {!detailsLoading && detailsError && (
          <Alert severity='error' sx={{ m: 2 }}>
            {typeof detailsError === 'string' ? detailsError : 'Unable to load contributing bills.'}
          </Alert>
        )}
        {!detailsLoading && !detailsError && (
          <TableContainer sx={{ maxHeight: 'calc(100vh - 260px)', overflowX: 'auto' }}>
            <Table stickyHeader size='small' sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Bill date</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Account / meter</TableCell>
                  <TableCell>Bill type</TableCell>
                  <TableCell align='right'>Total</TableCell>
                  <TableCell align='right'>Paid</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detailBills.map((bill) => (
                  <TableRow key={bill.id} hover>
                    <TableCell>{formatDate(bill.billDate)}</TableCell>
                    <TableCell>{bill.referenceNumber || '-'}</TableCell>
                    <TableCell>{bill.supplier || '-'}</TableCell>
                    <TableCell>{bill.accountMeter || '-'}</TableCell>
                    <TableCell>{bill.billType || '-'}</TableCell>
                    <TableCell align='right'>{fCurrency(bill.totalAmount)}</TableCell>
                    <TableCell align='right'>{fCurrency(bill.paidAmount)}</TableCell>
                    <TableCell>{bill.status || '-'}</TableCell>
                  </TableRow>
                ))}
                {!detailBills.length && (
                  <TableRow>
                    <TableCell colSpan={8} align='center'>
                      No approved bills contribute to this bucket.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={5} sx={{ fontWeight: 700 }}>
                    Bucket totals
                  </TableCell>
                  <TableCell align='right' sx={{ fontWeight: 700 }}>
                    {fCurrency(details?.totalCost ?? selectedPoint?.totalCost ?? 0)}
                  </TableCell>
                  <TableCell align='right' sx={{ fontWeight: 700 }}>
                    {fCurrency(detailPaidTotal)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </Drawer>
    </Page>
  );
}
